import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  HeartHandshake, Plus, Search, Eye, Pencil, Trash2,
  Upload, KeyRound, Users as UsersIcon, RefreshCw, BookText, Loader2, GraduationCap,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useOrgId } from '../../hooks/useOrgId';
import { useT } from '../../i18n/LanguageContext';
import { useFiscalYear } from '../../context/FiscalYearContext';
import { schoolApi } from '../../api/school';
import { PageHeader, EmptyState, LoadingState, Card } from '../../components/school/ui';
import { Pagination, SortableTH } from '../../components/school/Pagination';
import { SchoolAccountSearch } from '../../components/school/SchoolAccountSearch';
import { formatXOF } from '../../lib/utils';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { Badge } from '../../components/ui/badge';
import LedgerDialog from '../../components/LedgerDialog';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Localized family name following the global language switcher
const getName = (f, lang) => {
  if (!f) return '';
  if (lang === 'fr') return f.name_fr || f.name_en || f.name || f.name_pt || '';
  if (lang === 'pt') return f.name_pt || f.name_en || f.name || f.name_fr || '';
  return f.name_en || f.name || f.name_fr || f.name_pt || '';
};

const emptyForm = {
  name: '', name_fr: '', name_pt: '', mobile: '', email: '',
  address: '', vat_number: '', notes: '', relation: 'guardian', password: '',
  accounting_account_code: '',
};

export default function FamilyGuardiansPage() {
  const { canAdmin, user } = useAuth();
  const { t, lang } = useT();
  const orgId = useOrgId();
  const { selectedFY } = useFiscalYear();
  const fileRef = useRef(null);

  const [families, setFamilies] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState('code');
  const [sortDir, setSortDir] = useState('asc');
  const PAGE_SIZE = 50;

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [nextCode, setNextCode] = useState('');
  const [saving, setSaving] = useState(false);
  const [viewFamily, setViewFamily] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  // Ledger + children actions
  const [ledgerAccount, setLedgerAccount] = useState(null);
  const [childrenFamily, setChildrenFamily] = useState(null);
  const [childrenList, setChildrenList] = useState([]);
  const [childrenLoading, setChildrenLoading] = useState(false);

  // import
  const [importOpen, setImportOpen] = useState(false);
  const [importPreview, setImportPreview] = useState(null);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(null);

  const load = useCallback(async () => {
    if (!orgId) { setLoading(false); return; }
    try {
      setLoading(true);
      const data = await schoolApi.listFamilies(orgId, {
        search, sort_by: sortBy, sort_dir: sortDir,
        skip: (page - 1) * PAGE_SIZE, limit: PAGE_SIZE,
      });
      setFamilies(data.families || []);
      setTotal(data.total || 0);
    } catch (e) { toast.error('Failed to load families'); }
    finally { setLoading(false); }
  }, [orgId, search, sortBy, sortDir, page]);

  useEffect(() => { const id = setTimeout(load, 250); return () => clearTimeout(id); }, [load]);
  useEffect(() => { setPage(1); }, [search, sortBy, sortDir]);

  const onSort = (field) => {
    if (sortBy === field) { setSortDir((d) => (d === 'asc' ? 'desc' : 'asc')); }
    else { setSortBy(field); setSortDir('asc'); }
  };

  const openCreate = async () => {
    setEditing(null);
    setForm(emptyForm);
    setDialogOpen(true);
    try { const r = await schoolApi.familiesNextCode(orgId); setNextCode(r.next_code); }
    catch { setNextCode(''); }
  };

  const openEdit = (f) => {
    setEditing(f);
    setForm({
      name: f.name_en || f.name || '', name_fr: f.name_fr || '', name_pt: f.name_pt || '',
      mobile: f.mobile || '', email: f.email || '', address: f.address || '',
      vat_number: f.vat_number || '', notes: f.notes || '', relation: f.relation || 'guardian',
      accounting_account_code: f.accounting_account_code || '',
      password: '',
    });
    setNextCode(f.code);
    setDialogOpen(true);
  };

  // Fetch and show a guardian's linked children (students)
  const openChildren = async (f) => {
    setChildrenFamily(f);
    setChildrenList([]);
    setChildrenLoading(true);
    try {
      const data = await schoolApi.getFamily(f.id);
      setChildrenList(data.children || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('families.childrenError', 'Failed to load children'));
    } finally {
      setChildrenLoading(false);
    }
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error(t('common.name', 'Name') + ' ' + t('common.required', 'is required')); return; }
    setSaving(true);
    try {
      const payload = {
        organization_id: orgId,
        name: form.name.trim(),
        name_fr: form.name_fr.trim() || null,
        name_pt: form.name_pt.trim() || null,
        mobile: form.mobile.trim() || null,
        email: form.email.trim() || null,
        address: form.address.trim() || null,
        vat_number: form.vat_number.trim() || null,
        notes: form.notes.trim() || null,
        relation: form.relation || 'guardian',
        accounting_account_code: (form.accounting_account_code || '').trim() || null,
        password: form.password ? form.password : null,
      };
      if (editing) {
        await schoolApi.updateFamily(editing.id, payload);
        toast.success(t('common.saved', 'Saved'));
      } else {
        await schoolApi.createFamily(payload);
        toast.success(t('families.created', 'Family/Guardian created'));
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  const confirmDelete = async () => {
    try {
      await schoolApi.deleteFamily(deleteTarget.id);
      toast.success(t('common.deleted', 'Deleted'));
      setDeleteTarget(null); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to delete');
    }
  };

  const runMigration = async () => {
    try {
      const r = await schoolApi.migrateFamilies(orgId);
      toast.success(r.message);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Migration failed'); }
  };

  // ---- Import flow ----
  const onPickFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImportFile(file);
    setImportPreview(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(`${API}/school/families/import-excel/preview?organization_id=${orgId}`, fd);
      setImportPreview(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to preview file');
      setImportFile(null);
    }
    if (fileRef.current) fileRef.current.value = '';
  };

  const runImport = async () => {
    if (!importFile) return;
    setImporting(true);
    setImportProgress({ status: 'processing', processed: 0, total: importPreview?.total_rows || 0 });
    try {
      const fd = new FormData();
      fd.append('file', importFile);
      const r = await axios.post(`${API}/school/families/import-excel?organization_id=${orgId}`, fd);
      const jobId = r.data.job_id;
      if (!jobId) { throw new Error('No job started'); }

      // Poll job status until completed/failed
      let done = false;
      let polls = 0;
      const maxPolls = 400; // ~10 min safety cap
      while (!done && polls < maxPolls) {
        polls += 1;
        await new Promise((res) => setTimeout(res, 1500));
        let job;
        try {
          const jr = await axios.get(`${API}/school/families/import-jobs/${jobId}`);
          job = jr.data;
        } catch { continue; }
        setImportProgress({
          status: job.status,
          processed: job.processed || 0,
          total: job.total_ops || job.total || 0,
        });
        if (job.status === 'completed') {
          done = true;
          toast.success(job.message || 'Import complete');
          setImportOpen(false); setImportPreview(null); setImportFile(null); setImportProgress(null);
          load();
        } else if (job.status === 'failed') {
          done = true;
          toast.error(job.message || 'Import failed');
          setImportProgress(null);
        }
      }
      if (!done) {
        toast.info(t('families.importBackground', 'Import is still running in the background. Refresh the list shortly.'));
        setImportProgress(null);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Import failed');
      setImportProgress(null);
    } finally { setImporting(false); }
  };

  return (
    <div className="max-w-[1400px] mx-auto">
      <PageHeader
        title={t('nav.families', 'Family/Guardian')}
        subtitle={t('families.subtitle', 'Manage families & guardians (customer accounts) and their portal logins')}
        icon={HeartHandshake}
        actions={canAdmin ? (
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => setImportOpen(true)} className="gap-2" data-testid="families-import-button">
              <Upload className="w-4 h-4" /> {t('common.import', 'Import')}
            </Button>
            <Button onClick={openCreate} className="gap-2" data-testid="families-new-button">
              <Plus className="w-4 h-4" /> {t('families.new', 'New Family')}
            </Button>
          </div>
        ) : null}
      />

      <Card className="p-4 mb-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input placeholder={t('common.search', 'Search by name, code, phone, email...')} value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="families-search-input" />
          </div>
          <div className="flex items-center text-sm text-muted-foreground px-2">
            {total} {t('families.total', 'families')}
          </div>
        </div>
      </Card>

      <Card>
        {loading ? (
          <div className="p-4"><LoadingState /></div>
        ) : families.length === 0 ? (
          <EmptyState
            icon={HeartHandshake}
            title={t('common.noData', 'No families yet')}
            description={t('families.empty', 'Create a Family/Guardian or migrate from existing parents.')}
            actionLabel={canAdmin ? t('families.new', 'New Family') : undefined}
            onAction={canAdmin ? openCreate : undefined}
          />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
                    <SortableTH field="code" label={t('families.code', 'Code')} sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
                    <SortableTH field="name_en" label={t('common.name', 'Name')} sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
                    <SortableTH field="mobile" label={t('common.phone', 'Phone')} sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
                    <SortableTH field="email" label={t('common.email', 'Email')} sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
                    <th className="px-4 py-3 text-center">{t('families.students', 'Students')}</th>
                    <th className="px-4 py-3 text-center">{t('families.login', 'Login')}</th>
                    <th className="px-4 py-3 text-right">{t('families.balance', 'Balance')}</th>
                    <th className="px-4 py-3 text-right">{t('common.actions', 'Actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {families.map((f) => (
                    <tr key={f.id} className="border-b border-border last:border-0 hover:bg-muted/40" data-testid={`family-row-${f.id}`}>
                      <td className="px-4 py-3 tabular-nums text-muted-foreground font-mono">{f.code}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-xs font-semibold text-primary">{getName(f, lang)?.charAt(0)}</div>
                          <div>
                            <span className="font-medium">{getName(f, lang)}</span>
                            {lang !== 'en' && (f.name_en || f.name) && (
                              <p className="text-xs text-muted-foreground">{f.name_en || f.name}</p>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">{f.mobile || '-'}</td>
                      <td className="px-4 py-3 text-muted-foreground">{f.email || '-'}</td>
                      <td className="px-4 py-3 text-center">
                        <Badge variant="outline" className="gap-1"><UsersIcon className="w-3 h-3" /> {f.children_count ?? 0}</Badge>
                      </td>
                      <td className="px-4 py-3 text-center">
                        {f.has_login
                          ? <Badge variant="outline" className="text-emerald-700 border-emerald-200 bg-emerald-50 gap-1"><KeyRound className="w-3 h-3" /> {t('families.yes', 'Yes')}</Badge>
                          : <Badge variant="outline" className="text-muted-foreground">{t('families.no', 'No')}</Badge>}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums">{formatXOF(f.balance_usd || 0)}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost" size="icon"
                            className="h-8 w-8 text-muted-foreground hover:text-foreground hover:bg-muted"
                            onClick={() => setViewFamily(f)}
                            title={t('common.view', 'View')} aria-label={t('common.view', 'View')}
                            data-testid={`family-view-${f.id}`}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost" size="icon"
                            className="h-8 w-8 text-muted-foreground hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-950/30"
                            onClick={() => openChildren(f)}
                            title={t('families.viewChildren', 'View children')} aria-label={t('families.viewChildren', 'View children')}
                            data-testid={`family-children-${f.id}`}
                          >
                            <UsersIcon className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost" size="icon"
                            className="h-8 w-8 text-muted-foreground hover:text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-950/30"
                            onClick={() => setLedgerAccount(f)}
                            title={t('families.viewLedger', 'View Ledger')} aria-label={t('families.viewLedger', 'View Ledger')}
                            data-testid={`family-ledger-${f.id}`}
                          >
                            <BookText className="w-4 h-4" />
                          </Button>
                          {canAdmin && (
                            <Button
                              variant="ghost" size="icon"
                              className="h-8 w-8 text-muted-foreground hover:text-primary hover:bg-primary/10"
                              onClick={() => openEdit(f)}
                              title={t('common.edit', 'Edit')} aria-label={t('common.edit', 'Edit')}
                              data-testid={`family-edit-${f.id}`}
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                          )}
                          {canAdmin && (
                            <Button
                              variant="ghost" size="icon"
                              className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/30"
                              onClick={() => setDeleteTarget(f)}
                              title={t('common.delete', 'Delete')} aria-label={t('common.delete', 'Delete')}
                              data-testid={`family-delete-${f.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination page={page} pageSize={PAGE_SIZE} total={total} onPageChange={setPage} />
          </>
        )}
      </Card>

      {/* Create / Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="family-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? t('families.edit', 'Edit Family/Guardian') : t('families.new', 'New Family')}</DialogTitle>
            <DialogDescription>
              {editing ? t('families.code', 'Code') : t('families.nextCode', 'New account code')}: <span className="font-mono">{nextCode || '—'}</span>
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <Label>{t('families.nameEn', 'Name (English)')} *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="family-name-input" />
            </div>
            <div>
              <Label>{t('families.nameFr', 'Name (French)')}</Label>
              <Input value={form.name_fr} onChange={(e) => setForm({ ...form, name_fr: e.target.value })} data-testid="family-name-fr-input" />
            </div>
            <div>
              <Label>{t('families.namePt', 'Name (Portuguese)')}</Label>
              <Input value={form.name_pt} onChange={(e) => setForm({ ...form, name_pt: e.target.value })} data-testid="family-name-pt-input" />
            </div>
            <div><Label>{t('common.phone', 'Phone')}</Label><Input value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} data-testid="family-mobile-input" /></div>
            <div><Label>{t('common.email', 'Email')}</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="family-email-input" /></div>
            <div className="sm:col-span-2"><Label>{t('families.address', 'Address')}</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
            <div><Label>{t('families.vat', 'VAT / Tax No.')}</Label><Input value={form.vat_number} onChange={(e) => setForm({ ...form, vat_number: e.target.value })} /></div>
            <div className="sm:col-span-2">
              <Label>{t('families.accountingAccount', 'Accounting Account Number')}</Label>
              <SchoolAccountSearch orgId={orgId} value={form.accounting_account_code} onChange={(v) => setForm({ ...form, accounting_account_code: v })} placeholder={editing ? (editing.code || 'Account code') : 'Defaults to this guardian\u2019s own account'} testid="family-accounting-account" />
              <p className="text-xs text-muted-foreground mt-1">{t('families.accountingAccountHint', 'Used as the receivable account when posting this guardian\u2019s fees invoices. Leave blank to use the guardian\u2019s own account; the global fallback applies only if neither is set.')}</p>
            </div>
            <div className="sm:col-span-2"><Label>{t('families.notes', 'Notes')}</Label><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
          </div>

          <div className="border-t border-border pt-4 mt-2">
            <p className="text-sm font-semibold mb-1 flex items-center gap-2"><KeyRound className="w-4 h-4" /> {t('families.portalLogin', 'Portal Login (Parent role)')}</p>
            <p className="text-[11px] text-muted-foreground mb-2">
              {editing
                ? t('families.loginEditHint', 'Set a password to (re)create/reset the login. Email above is the username.')
                : t('families.loginCreateHint', 'Enter email + password to create a parent login so the family can see their children.')}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>{t('people.password', 'Password')}</Label>
                <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder={editing && form.email ? '••••••' : ''} data-testid="family-password-input" />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>{t('common.cancel', 'Cancel')}</Button>
            <Button onClick={save} disabled={saving} data-testid="family-save-button">{saving ? t('common.saving', 'Saving...') : t('common.save', 'Save')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View dialog */}
      <Dialog open={!!viewFamily} onOpenChange={(o) => !o && setViewFamily(null)}>
        <DialogContent data-testid="family-view-dialog">
          <DialogHeader><DialogTitle>{viewFamily ? getName(viewFamily, lang) : ''}</DialogTitle></DialogHeader>
          {viewFamily && (
            <div className="space-y-2 text-sm">
              <Row label={t('families.code', 'Code')} value={viewFamily.code} />
              <Row label={t('families.nameEn', 'Name (English)')} value={viewFamily.name_en || viewFamily.name} />
              <Row label={t('families.nameFr', 'Name (French)')} value={viewFamily.name_fr} />
              <Row label={t('families.namePt', 'Name (Portuguese)')} value={viewFamily.name_pt} />
              <Row label={t('common.phone', 'Phone')} value={viewFamily.mobile} />
              <Row label={t('common.email', 'Email')} value={viewFamily.email} />
              <Row label={t('families.students', 'Students')} value={String(viewFamily.children_count ?? 0)} />
              <Row label={t('families.login', 'Login')} value={viewFamily.has_login ? t('families.yes', 'Yes') : t('families.no', 'No')} />
              <Row label={t('families.balance', 'Balance')} value={formatXOF(viewFamily.balance_usd || 0)} />
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Children (linked students) dialog */}
      <Dialog open={!!childrenFamily} onOpenChange={(o) => { if (!o) { setChildrenFamily(null); setChildrenList([]); } }}>
        <DialogContent data-testid="family-children-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UsersIcon className="w-5 h-5" />
              {t('families.childrenOf', 'Children of')} {childrenFamily ? getName(childrenFamily, lang) : ''}
            </DialogTitle>
            <DialogDescription>
              {childrenFamily?.code} · {childrenLoading ? t('common.loading', 'Loading...') : `${childrenList.length} ${t('families.students', 'Students')}`}
            </DialogDescription>
          </DialogHeader>
          {childrenLoading ? (
            <div className="flex items-center justify-center py-10 text-muted-foreground">
              <Loader2 className="w-6 h-6 animate-spin" />
            </div>
          ) : childrenList.length === 0 ? (
            <div className="text-center py-10 text-muted-foreground">
              <GraduationCap className="w-10 h-10 mx-auto mb-2 opacity-40" />
              <p className="text-sm">{t('families.noChildren', 'No students linked to this guardian')}</p>
            </div>
          ) : (
            <div className="space-y-2 max-h-[60vh] overflow-auto">
              {childrenList.map((c) => (
                <div key={c.id} className="flex items-center gap-3 rounded-md border border-border p-2.5" data-testid={`family-child-${c.id}`}>
                  <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center text-sm font-semibold text-primary shrink-0">
                    {c.name?.charAt(0) || '?'}
                  </div>
                  <div className="min-w-0">
                    <p className="font-medium truncate">{c.name}</p>
                    {c.admission_no && <p className="text-xs text-muted-foreground font-mono">{c.admission_no}</p>}
                  </div>
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setChildrenFamily(null); setChildrenList([]); }}>{t('common.close', 'Close')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Guardian ledger */}
      <LedgerDialog
        account={ledgerAccount}
        organizationId={orgId}
        open={!!ledgerAccount}
        onClose={() => setLedgerAccount(null)}
        userRole={user?.role}
        fyId={selectedFY?.id}
      />

      {/* Delete confirm */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('common.delete', 'Delete')}?</AlertDialogTitle>
            <AlertDialogDescription>
              {t('families.deleteHint', 'This will remove the family/guardian and its portal login. Blocked if it has a balance, linked students, or transactions.')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel', 'Cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-family">{t('common.delete', 'Delete')}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Import dialog */}
      <Dialog open={importOpen} onOpenChange={(o) => { setImportOpen(o); if (!o) { setImportPreview(null); setImportFile(null); } }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="family-import-dialog">
          <DialogHeader>
            <DialogTitle>{t('families.importTitle', 'Import Families/Guardians')}</DialogTitle>
            <DialogDescription>
              {t('families.importHint', 'Upload an .xlsx with columns: guardians_name, guardians_mobile, guardians_email, password. Existing families are matched by email and updated; new ones get a 4111 code and a parent login.')}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="flex items-center justify-between gap-3">
              <Button variant="outline" onClick={runMigration} className="gap-2" data-testid="family-migrate-button">
                <RefreshCw className="w-4 h-4" /> {t('families.migrate', 'Migrate from existing parents')}
              </Button>
            </div>

            <div className="border-2 border-dashed border-border rounded-lg p-6 text-center">
              <input ref={fileRef} type="file" accept=".xlsx,.xls" onChange={onPickFile} className="hidden" id="family-file-input" data-testid="family-file-input" />
              <Upload className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
              <Button variant="outline" onClick={() => fileRef.current?.click()} data-testid="family-choose-file-button">
                {importFile ? importFile.name : t('families.chooseFile', 'Choose Excel file')}
              </Button>
            </div>

            {importPreview && (
              <div className="space-y-2">
                <div className="flex gap-4 text-sm">
                  <span><strong>{importPreview.total_rows}</strong> {t('families.rows', 'rows')}</span>
                  <span className="text-emerald-600"><strong>{importPreview.new_families}</strong> {t('families.toCreate', 'to create')}</span>
                  <span className="text-amber-600"><strong>{importPreview.update_families}</strong> {t('families.toUpdate', 'to update')}</span>
                </div>
                <div className="max-h-60 overflow-y-auto border border-border rounded">
                  <table className="w-full text-xs">
                    <thead className="bg-muted/50 sticky top-0">
                      <tr>
                        <th className="px-2 py-1 text-left">{t('families.action', 'Action')}</th>
                        <th className="px-2 py-1 text-left">{t('common.name', 'Name')}</th>
                        <th className="px-2 py-1 text-left">{t('common.phone', 'Phone')}</th>
                        <th className="px-2 py-1 text-left">{t('common.email', 'Email')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {importPreview.preview_data.map((r, i) => (
                        <tr key={i} className="border-t border-border">
                          <td className="px-2 py-1"><Badge variant="outline" className={r.action === 'create' ? 'text-emerald-700' : 'text-amber-700'}>{r.action}</Badge></td>
                          <td className="px-2 py-1">{r.name}</td>
                          <td className="px-2 py-1">{r.mobile || '-'}</td>
                          <td className="px-2 py-1">{r.email || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {importPreview.errors?.length > 0 && (
                  <p className="text-xs text-red-600">{importPreview.errors.length} warning(s): {importPreview.errors.slice(0, 3).join('; ')}</p>
                )}
              </div>
            )}
          </div>

          {importProgress && (
            <div className="space-y-1" data-testid="family-import-progress">
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>{importProgress.status === 'processing' ? t('families.importing', 'Importing...') : importProgress.status}</span>
                <span>{importProgress.processed}{importProgress.total ? ` / ${importProgress.total}` : ''}</span>
              </div>
              <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
                <div
                  className="h-full bg-primary transition-all duration-300"
                  style={{ width: importProgress.total ? `${Math.min(100, Math.round((importProgress.processed / importProgress.total) * 100))}%` : '40%' }}
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setImportOpen(false)} disabled={importing}>{t('common.cancel', 'Cancel')}</Button>
            <Button onClick={runImport} disabled={!importPreview || importing} data-testid="family-run-import-button">
              {importing ? t('common.importing', 'Importing...') : t('families.confirmImport', 'Confirm Import')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Row = ({ label, value }) => (
  <div className="flex justify-between border-b border-border py-1.5"><span className="text-muted-foreground">{label}</span><span className="font-medium">{value || '-'}</span></div>
);
